import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { ChatRoom } from '../types/chat';
import './Chat.css';
import logoKT from '../assets/images/logo_kt.svg';
import logoSKT from '../assets/images/logo_skt.svg';
import logoUplus from '../assets/images/logo_uplus.svg';

interface ChatProps {
  onChatRoomClick: (roomId: string) => void;
}

interface ChatRoomWithDetails extends ChatRoom {
  counselor_name?: string;
  last_message?: string;
  unread_count?: number;
  product_image?: string;
  product_name?: string;
  carrier?: string;
  product_type?: string;
}

const Chat: React.FC<ChatProps> = ({ onChatRoomClick }) => {
  const { customer } = useAuth();
  const [chatRooms, setChatRooms] = useState<ChatRoomWithDetails[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer) {
      fetchChatRooms();

      // Realtime 구독 설정
      const channel = supabase
        .channel('chat_rooms_changes')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'chat_rooms',
            filter: `customer_id=eq.${customer.id}`
          },
          () => {
            fetchChatRooms();
          }
        )
        .on('postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          () => {
            fetchChatRooms(); // 새 메시지가 오면 채팅방 목록 갱신
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const fetchChatRooms = async () => {
    if (!customer) return;

    setLoading(true);
    try {
      // 1. 고객의 채팅방 가져오기 (나간 채팅방 제외)
      const { data: roomsData, error: roomsError } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('customer_id', customer.id)
        .is('customer_exited_at', null)  // 고객이 나간 채팅방 제외
        .order('created_at', { ascending: false });

      if (roomsError) {
        console.error('Error fetching chat rooms:', roomsError);
        setChatRooms([]);
        return;
      }

      // 2. 각 채팅방의 상세 정보 가져오기
      const roomsWithDetails = await Promise.all((roomsData || []).map(async (room) => {
        // chat_rooms에서 직접 가져온 상품 정보
        const carrier = room.carrier || '';
        const productType = room.product_type || '';
        const phoneModelId = room.phone_model_id || null;

        // 상담원 이름 가져오기
        let counselorName = '상담원 배정 대기';
        if (room.counselor_id) {
          const { data: counselorData, error: counselorError } = await supabase
            .from('profiles')
            .select('name, nickname')
            .eq('id', room.counselor_id)
            .single();

          if (counselorError) {
            console.error('Error fetching counselor:', counselorError);
          }

          if (counselorData) {
            counselorName = counselorData.nickname || counselorData.name || '상담원';
          }
        }

        // 마지막 메시지 가져오기
        const { data: lastMessageData, error: messageError } = await supabase
          .from('messages')
          .select('content, message_type, created_at')
          .eq('room_id', room.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (messageError && messageError.code !== 'PGRST116') {
          // PGRST116 = no rows returned, which is fine
          console.error('Error fetching last message:', messageError);
        }

        let lastMessage = '';
        if (lastMessageData) {
          if (lastMessageData.message_type === 'text') {
            lastMessage = lastMessageData.content;
          } else if (lastMessageData.message_type === 'image') {
            lastMessage = '📷 이미지';
          } else if (lastMessageData.message_type === 'file') {
            lastMessage = '📎 파일';
          } else if (lastMessageData.message_type === 'pdf') {
            lastMessage = '📄 PDF';
          }
        }

        // 안읽은 메시지 개수 (고객이 받은 메시지 중 안읽은 것)
        const { count: unreadCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('room_id', room.id)
          .eq('is_read', false)
          .neq('sender_id', customer.id);

        // 핸드폰 상품 이미지 가져오기
        let productImage = '';
        let productName = '';

        if (phoneModelId) {
          const { data: phoneData, error: phoneError } = await supabase
            .from('phone_models')
            .select('image_url, model_name, available_colors')
            .eq('id', phoneModelId)
            .single();

          if (phoneError) {
            console.error('Error fetching phone model:', phoneError);
          }

          if (phoneData) {
            productName = phoneData.model_name || '';

            // 이미지 우선순위: 1. image_url, 2. available_colors 첫번째 색상 이미지
            if (phoneData.image_url) {
              productImage = phoneData.image_url;
            } else if (phoneData.available_colors && phoneData.available_colors.length > 0) {
              productImage = phoneData.available_colors[0].image_url || '';
            }
          }
        }

        return {
          ...room,
          counselor_name: counselorName,
          last_message: lastMessage,
          unread_count: unreadCount || 0,
          product_image: productImage,
          product_name: productName,
          carrier: carrier,
          product_type: productType
        };
      }));

      setChatRooms(roomsWithDetails);
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = (roomId: string) => {
    onChatRoomClick(roomId);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isYesterday) {
      return '어제';
    }

    return date.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
  };

  const getCarrierLogo = (carrier?: string) => {
    switch (carrier) {
      case 'KT':
        return logoKT;
      case 'SKT':
      case 'SK':
        return logoSKT;
      case 'LG':
      case 'LG U+':
      case 'LGU+':
        return logoUplus;
      default:
        return null;
    }
  };

  if (!customer) {
    return (
      <div className="chat-container">
        <div className="chat-content">
          <h1 className="chat-title">채팅</h1>
          <div className="chat-empty">
            <p>로그인이 필요합니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-content">
        <h1 className="chat-title">채팅</h1>

        {loading ? (
          <div className="chat-loading">로딩 중...</div>
        ) : chatRooms.length === 0 ? (
          <div className="chat-empty">
            <p>진행 중인 채팅이 없습니다.</p>
            <p className="chat-empty-subtitle">문의사항이 있으시면 고객센터로 연락해주세요.</p>
          </div>
        ) : (
          <div className="chat-list">
            {chatRooms.map((room) => {
              const carrierLogo = getCarrierLogo(room.carrier);
              const isInternetTv = room.product_type === 'internet' || room.product_type === 'tv';

              return (
                <button key={room.id} className="chat-item" onClick={() => handleChatClick(room.id)}>
                  <div className="chat-thumbnail">
                    {isInternetTv && carrierLogo ? (
                      <img src={carrierLogo} alt={room.carrier || '통신사'} className="chat-thumbnail-image" />
                    ) : room.product_image ? (
                      <img src={room.product_image} alt={room.product_name || '상품'} className="chat-thumbnail-image" />
                    ) : (
                      <div className="chat-thumbnail-placeholder">📱</div>
                    )}
                  </div>
                <div className="chat-info">
                  <div className="chat-header">
                    <p className="chat-room-title">{room.counselor_name || '상담원'}</p>
                    <div className="chat-header-right">
                      {room.last_message_at && (
                        <span className="chat-time">{formatDate(room.last_message_at)}</span>
                      )}
                    </div>
                  </div>
                  <div className="chat-bottom-row">
                    <p className="chat-last-message">{room.last_message || '메시지 없음'}</p>
                    {room.unread_count !== undefined && room.unread_count > 0 && (
                      <span className="chat-unread-badge">{room.unread_count}</span>
                    )}
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
